import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useGetCustomer, useMutateUpdateCustomer } from '../../api/contact.ts';
import { useMemo, useState } from 'react';
import { UpdateCustomerRequest } from '../../models/contact.models.ts';
import { AuthDialog } from '../auth-dialog/auth-dialog.tsx';
import {
  UpdateContactInformationValidationSchema,
  UpdateContactInformationValues,
} from './constants/schema.ts';
import './contact-information.scss';
import { useTitle } from '../../hooks/useTitle.ts';

const labelStyle = {
  fontSize: '14px',
  marginBottom: '4px',
};

export const ContactInformationView = () => {
  useTitle('Contact Information - Immersive Homes');
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);

  const { data: userAttributes, loading } = useGetCustomer();
  const mutation = useMutateUpdateCustomer();

  const postSubmitErrors = mutation.error?.message;

  const handleCloseSnackbar = () => {
    setOpenSnackBar(false);
  };

  const initialValues = useMemo(() => {
    return {
      firstName: userAttributes?.firstName || '',
      lastName: userAttributes?.lastName || '',
      phone: userAttributes?.phoneNumber?.slice(2) || '',
      textOrEmailPreference: userAttributes?.contactPreferences || '',
    } as UpdateContactInformationValues;
  }, [userAttributes]);

  return (
    <Box className="contact-information-page">
      <>
        <Typography variant="h3">Contact Information</Typography>
        <Box className="container">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Formik
              initialValues={initialValues}
              validationSchema={UpdateContactInformationValidationSchema}
              validateOnBlur={true}
              validateOnMount={true}
              validateOnChange={true}
              enableReinitialize={true}
              onSubmit={(values) => {
                const req: UpdateCustomerRequest = {
                  firstName: values.firstName,
                  lastName: values.lastName,
                  phoneNumber: `+1${values.phone}`,
                  contactPreferences: values.textOrEmailPreference,
                };
                mutation.mutate(req, {
                  onSuccess: () => {
                    setOpenSnackBar(true);
                  },
                });
              }}
            >
              {({
                values,
                touched,
                errors,
                handleChange,
                setFieldValue,
                isValid,
              }: FormikProps<UpdateContactInformationValues>) => (
                <Form>
                  <>
                    <Box
                      className="field-group"
                      data-testid="signup-form-page-1"
                    >
                      <Box className="field">
                        <InputLabel sx={labelStyle}>First Name</InputLabel>
                        <TextField
                          disabled={mutation.isPending || loading}
                          className="text-input"
                          fullWidth
                          id="firstName"
                          name="firstName"
                          placeholder="First Name"
                          onChange={handleChange}
                          value={values.firstName}
                          error={touched.firstName && Boolean(errors.firstName)}
                        />
                      </Box>
                      <Box className="field">
                        <InputLabel sx={labelStyle}>Last Name</InputLabel>
                        <TextField
                          disabled={mutation.isPending || loading}
                          fullWidth
                          className="text-input"
                          id="lastName"
                          name="lastName"
                          placeholder="Last Name"
                          onChange={handleChange}
                          value={values.lastName}
                          error={touched.lastName && Boolean(errors.lastName)}
                        />
                      </Box>
                    </Box>
                    <Box className="field-group">
                      <Box className="field">
                        <InputLabel sx={labelStyle}>
                          Cell Phone Number
                        </InputLabel>
                        <TextField
                          disabled={mutation.isPending || loading}
                          fullWidth
                          className="text-input"
                          id="phone"
                          name="phone"
                          placeholder="Cell Phone Number"
                          type="phone"
                          onChange={handleChange}
                          value={values.phone}
                          error={touched.phone && Boolean(errors.phone)}
                        />
                      </Box>
                    </Box>

                    <Box
                      className="field-group"
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <Box>
                        <Typography>Preferred Contact Method</Typography>
                        <FormControl
                          error={
                            touched.textOrEmailPreference &&
                            Boolean(errors.textOrEmailPreference)
                          }
                        >
                          <RadioGroup
                            aria-labelledby="radio-buttons-group-label"
                            name="textOrEmailPreference"
                            value={values.textOrEmailPreference}
                            onChange={(event) => {
                              void setFieldValue(
                                'textOrEmailPreference',
                                event.currentTarget.value
                              );
                            }}
                          >
                            <FormControlLabel
                              disabled={mutation.isPending || loading}
                              value="SMS_TEXT"
                              control={<Radio />}
                              label="Text/SMS"
                            />
                            <FormControlLabel
                              disabled={mutation.isPending || loading}
                              value="EMAIL"
                              control={<Radio />}
                              label="Email"
                            />
                            <FormControlLabel
                              disabled={mutation.isPending || loading}
                              value="PHONE_CALL"
                              control={<Radio />}
                              label="Phone Call"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                      {postSubmitErrors && (
                        <Box className="error-text">
                          <Typography sx={{ borderBottom: 'solid 1px white' }}>
                            Something's wrong:{' '}
                          </Typography>
                          <ul>
                            <li>{postSubmitErrors}</li>
                          </ul>
                        </Box>
                      )}
                    </Box>
                    <Button
                      disabled={!isValid || mutation.isPending || loading}
                      type="submit"
                      variant="contained"
                      className="update-button"
                    >
                      Update
                    </Button>

                    <Button
                      disabled={mutation.isPending || loading}
                      type="button"
                      onClick={() => {
                        setOpenForgotPassword(true);
                      }}
                      variant="text"
                      className="update-button"
                    >
                      Change password
                    </Button>
                    <AuthDialog
                      open={openForgotPassword}
                      onClose={() => setOpenForgotPassword(false)}
                      payload="forgot_password"
                    />
                  </>
                  <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={openSnackBar}
                    onClose={handleCloseSnackbar}
                    message="Successfully updated contact information"
                    key={'snackbar'}
                  />
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </>
    </Box>
  );
};


